import { Card, BlockStack, Text, InlineStack, Banner } from '@shopify/polaris';
import { ASSETS } from '../../config';
import { SearchIcon, ChatIcon, NoteIcon } from '@shopify/polaris-icons';
import { ONBOARDING_VALUE_PROPS } from '../../theme';
import { StaggeredList } from '../ui';
import { BRAND_COLORS } from '../../theme/colors';

const VALUE_PROP_META = [
  {
    icon: SearchIcon,
    accent: BRAND_COLORS.accent,
    accentLight: `rgba(139, 92, 246, 0.08)`,
    label: 'AI Visibility',
  },
  {
    icon: ChatIcon,
    accent: BRAND_COLORS.accentStrong,
    accentLight: `rgba(124, 58, 237, 0.08)`,
    label: 'Discoverability Score',
  },
  {
    icon: NoteIcon,
    accent: BRAND_COLORS.ctaBlue,
    accentLight: `rgba(8, 145, 178, 0.08)`,
    label: 'Actionable Suggestions',
  },
] as const;

/**
 * T018: WelcomeStep — Step 1 of the onboarding wizard.
 * Value proposition + "Nothing starts until you click Start Scan".
 * Uses theme config for value props; staggered entrance animation.
 *
 * @see documents/ui-enhancement-spec.md §12.2 (Step 1: Welcome)
 */
interface WelcomeStepProps {
  onNext: () => void;
}

export function WelcomeStep({ onNext: _onNext }: Readonly<WelcomeStepProps>) {
  return (
    <BlockStack gap="400">
      <Card>
        <div style={{ background: 'var(--z-gradient-hero-light)', borderRadius: 'var(--p-border-radius-200)', padding: 'var(--p-space-800) var(--p-space-600)', textAlign: 'center' }}>
          <BlockStack gap="400">
            <img
              src={ASSETS.logoSvg}
              alt="Zelium AI"
              style={{ height: '48px', width: 'auto', display: 'block', margin: '0 auto' }}
            />
            <h2 style={{
              margin: '0 0 var(--p-space-400)',
              fontSize: 'var(--p-font-size-750)',
              fontWeight: 700,
              lineHeight: '1.35',
            }} className="onboard-animated-brand-heading">
              Welcome to Zelium AI
            </h2>

            <Text as="p" variant="bodyLg">
              Zelium helps Shopify merchants understand how their products appear to AI-powered
              shopping assistants — and what to improve so customers can find them.
            </Text>
          </BlockStack>
        </div>
      </Card>

      <StaggeredList staggerDelay={0.08} baseDelay={200}>
        {ONBOARDING_VALUE_PROPS.map((prop, i) => {
          const meta = VALUE_PROP_META[i];
          const IconComponent = meta.icon;
          return (
            <div
              key={prop}
              style={{
                background: `linear-gradient(135deg, ${meta.accentLight} 0%, rgba(255,255,255,0) 60%)`,
                border: `1px solid ${meta.accent}33`,
                borderLeft: `4px solid ${meta.accent}`,
                borderRadius: '12px',
                padding: 'var(--p-space-400) var(--p-space-500)',
                boxShadow: `0 2px 8px ${meta.accent}14`,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              }}
            >
              <InlineStack gap="400" blockAlign="center" align="start">
                <div style={{
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${meta.accent} 0%, ${meta.accent}bb 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${meta.accent}40`,
                }}>
                  <IconComponent width={22} height={22} style={{ fill: BRAND_COLORS.white }} />
                </div>
                <BlockStack gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold" tone="subdued">
                    {meta.label}
                  </Text>
                  <Text as="p" variant="bodyMd" fontWeight="medium">
                    {prop}
                  </Text>
                </BlockStack>
              </InlineStack>
            </div>
          );
        })}
      </StaggeredList>

      <Banner tone="info">
        <Text as="p" variant="bodyMd">
          Nothing starts until you click Start Scan. We&apos;ll explain exactly what data we
          access and ask for your permission first.
        </Text>
      </Banner>
    </BlockStack>
  );
}
